import { useAuth } from '@/context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { 
  HomeIcon, 
  UsersIcon, 
  ClockIcon, 
  DocumentTextIcon, 
  Bars3Icon,
  XMarkIcon,
  BuildingOfficeIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { getInitials } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Company', href: '/company', icon: BuildingOfficeIcon, roles: ['SUPERADMIN'] },
  { name: 'Employees', href: '/employees', icon: UsersIcon, roles: ['ADMIN', 'HR', 'SUPERADMIN'] },
  { name: 'Attendance', href: '/attendance', icon: ClockIcon },
  { name: 'Leaves', href: '/leaves', icon: DocumentTextIcon },
  { name: 'Change Password', href: '/change-password', icon: KeyIcon },
];

export default function Layout({ children }: LayoutProps) {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  console.log(user,"666777777")

  useEffect(() => {
    if (!loading) {
      const isAuthPage = pathname.startsWith('/auth');
      console.log('Layout useEffect triggered. User:', user, 'Pathname:', pathname);
      
      if (!user && !isAuthPage) {
        console.log('No user, redirecting to login');
        navigate('/auth/login', { replace: true });
      } else if (user && isAuthPage) {
        console.log('User logged in, redirecting to dashboard');
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, loading, pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user && !pathname.startsWith('/auth')) {
    return null;
  }

  if (pathname.startsWith('/auth')) {
    return <>{children}</>;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  const filteredNavigation = navigation.filter(item => 
    !item.roles || item.roles.includes(user?.role || '')
  );



  // useEffect(() => {
  //   navigator.geolocation.getCurrentPosition((position) => {
  //     console.log('User location:', position.coords);
  //   }, (error) => {
  //     console.error('Error getting location:', error);
  //   }); 

  // }, []);
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 flex-shrink-0 items-center justify-between px-4 border-b">
            {/* <div className="flex items-center gap-2">
              <img 
                src="https://admindev.thingstodooo.in/assets/nowthingstodo-4e60bdab.png" 
                alt="Logo" 
                className="h-8 w-[7rem] object-contain"
              />
              <h1 className="text-xl font-bold text-gray-900">Attendance System</h1>
            </div> */}
            <button onClick={() => setSidebarOpen(false)}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {filteredNavigation.map((item) => {
              const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 h-6 w-6 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 flex-shrink-0 items-center px-4 border-b">
            <div className="flex items-center gap-2">
              {/* <img 
                src="https://admindev.thingstodooo.in/assets/nowthingstodo-4e60bdab.png" 
                alt="Logo" 
                className="h-8 w-[7rem] object-contain"
              /> */}
              <h1 className="text-xl font-bold text-gray-900">Attendance System</h1>
            </div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {filteredNavigation.map((item) => {
              const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="mr-3 h-6 w-6 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col lg:pl-64">
        {/* Top header */}
        <div className="sticky top-0 z-40 flex h-16 flex-shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Profile dropdown */}
              <div className="flex items-center gap-x-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-medium text-white">
                  {user && getInitials(user.firstName, user.lastName)}
                </div>
                <div className="hidden lg:block">
                  <div className="text-sm font-medium text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="text-xs text-gray-500">{user?.employeeId}</div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}